import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs/internal/Subscription';
import { CoachingSessionDTO } from '../../dtos/coaching-session.dto';
import { CoachingLogService } from '../../services/coaching-log/coaching-log.service';
import { GlaiService } from '../../services/glai/glai.service';
import { CURRENT_SCHOOL_YEAR } from '../../services/school-year/school-year.utilities';
import { ObsvSessionSubmissionResponse } from '../../types/responses/coaching-log.responses';
import { CoachingLogPrevSubmissionsModalComponent } from '../coaching-log-prev-submissions-modal/coaching-log-prev-submissions-modal.component';

@Component({
  selector: 'app-coaching-log-prev-submissions',
  standalone: false,
  templateUrl: './coaching-log-prev-submissions.component.html',
  styleUrl: './coaching-log-prev-submissions.component.scss',
})
export class CoachingLogPrevSubmissionsComponent implements OnInit, OnDestroy {
  @ViewChild('submissionModal')
  submissionModal: CoachingLogPrevSubmissionsModalComponent;

  @Input() prevSubmissionsActive: boolean;

  @Input() currentSession: CoachingSessionDTO;

  @Input() attendeeId: number;

  @Output() readonly hidePrevSubmissionsEvent = new EventEmitter<boolean>();

  subs: Subscription[] = [];

  sessionSubmissionsData: ObsvSessionSubmissionResponse[];

  selectedSessionSubIdx: number;

  checkingExistingSummary = true;

  loadingSummary = false;

  smartSummary: string | null = null;

  summaryFeedbackId: number | null = null;

  summaryUserFeedback: string | null = null;

  summaryError: string | null = null;

  firstDayOfSchoolYear = DateTime.fromISO(CURRENT_SCHOOL_YEAR.start_date)
    .startOf('day')
    .toSeconds();

  firstDayOfLogSchoolYear = 0;

  constructor(
    public coachingService: CoachingLogService,
    private glaiService: GlaiService
  ) {}

  ngOnInit(): void {
    const attendees = this.currentSession?.attendees ?? [];

    const logDate = DateTime.fromSeconds(this.currentSession.startDatetime);
    let logYear = logDate.year;
    if (logDate.month < 7) {
      logYear -= 1;
    }
    this.firstDayOfSchoolYear = DateTime.fromObject({
      year: logYear,
      month: 7,
      day: 1,
    })
      .startOf('day')
      .toSeconds();

    this.glaiService.findObservationSummary(this.currentSession.id).subscribe({
      next: ({ item }) => {
        if (item) {
          this.smartSummary = JSON.parse(item.response_data).content;
          this.summaryFeedbackId = item.id;
          this.summaryUserFeedback = item.user_feedback;
        }
        this.checkingExistingSummary = false;
      },
      error: () => {
        this.checkingExistingSummary = false;
      },
    });

    this.subs.push(
      this.coachingService
        .getPreviousObsvSessions(
          this.currentSession.creatorUserId,
          this.attendeeId,
          this.firstDayOfSchoolYear,
          this.currentSession.startDatetime - 1,
          50,
          attendees
        )
        .subscribe((sessions) => {
          const filtered = (sessions ?? []).filter(
            (session) => session.type.view === 'observation'
          );
          if (filtered.length > 0) {
            this.getSessionSubmissionData(filtered);
          } else {
            this.sessionSubmissionsData = [];
          }
        })
    );
  }

  togglePrevSubmissions(): void {
    this.hidePrevSubmissionsEvent.emit(true);
  }

  getSessionSubmissionData(coachlogs: CoachingSessionDTO[]) {
    const coachlogIds: number[] = [];
    coachlogs.forEach((session: CoachingSessionDTO) => {
      coachlogIds.push(session.id);
    });
    this.coachingService
      .getPrevObsvData(coachlogIds)
      .subscribe((sessionData) => {
        this.sessionSubmissionsData =
          sessionData.items as ObsvSessionSubmissionResponse[];
      });
  }

  showSubmissionModal(index: number) {
    this.selectedSessionSubIdx = index;
    this.submissionModal.modal.open();
  }

  getSmartSummary(): void {
    if (this.loadingSummary) return;
    this.loadingSummary = true;
    this.smartSummary = null;
    this.summaryFeedbackId = null;
    this.summaryUserFeedback = null;
    this.summaryError = null;
    const coachlogIds = this.sessionSubmissionsData.map((s) => s.session.id);
    this.glaiService
      .summarizeObservationHistory(
        this.currentSession.id,
        this.attendeeId,
        coachlogIds
      )
      .subscribe({
        next: (response) => {
          if (response.item) {
            this.smartSummary = JSON.parse(response.item.response_data).content;
            this.summaryFeedbackId = response.item.id;
            this.summaryUserFeedback = response.item.user_feedback;
          }
          this.loadingSummary = false;
        },
        error: () => {
          this.summaryError =
            'An error occurred while generating the summary. Please try again.';
          this.loadingSummary = false;
        },
      });
  }

  submitSummaryFeedback(direction: string): void {
    if (!this.summaryFeedbackId) return;
    this.summaryUserFeedback = direction;
    this.glaiService
      .submitGlaiContentFeedback(this.summaryFeedbackId, direction)
      .subscribe();
  }

  dismissSmartSummary(): void {
    this.smartSummary = null;
    this.summaryFeedbackId = null;
    this.summaryUserFeedback = null;
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
    this.subs = [];
  }
}
